// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import * as React from 'react';
import { Box, Grid, Typography } from '../../lib/base';
import * as routes from '../../routes';
import { Image } from '../common/image';

interface Props {
  readonly name: string;
  readonly title: string;
  readonly linkedInUrl?: string;
  readonly instagramHandle?: string;
  readonly avatarImagePath: string;
  readonly description: string;
}
function TeamMemberCard({ name, title, description, avatarImagePath, instagramHandle, linkedInUrl }: Props) {
  return (
    <Grid sx={{ maxWidth: { xs: '100%', md: '48%' }, p: 1 }}>
      <Grid container display="flex" flexDirection="column" alignItems="center">
        <Typography sx={{ mb: 0.5, textAlign: 'center' }} variant="h4">
          {name}
        </Typography>
        <Typography sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }} variant="h5">
          {title}
        </Typography>
        <Box sx={{ marginBottom: 4, position: 'relative' }}>
          <Image
            sx={{
              marginRight: 'auto',
              marginLeft: 'auto',
              borderRadius: '50%',
              width: '50%',
              height: '50%',
              marginBottom: 0,
              boxShadow: '0 10px 30px 1px rgba(0,0,0,.3)',
            }}
            width={225}
            height={225}
            imageUrl={avatarImagePath}
            altText={`${name} headshot`}
          />
          <Box sx={{ position: 'absolute', top: 200, right: 90, display: 'flex', flexDirection: 'row', gap: '5px' }}>
            {linkedInUrl && (
              <a href={linkedInUrl} target="_blank" rel="noreferrer">
                <img src={routes.makePublic('/linkedin.png')} alt="LinkedIn" width="30px" />
              </a>
            )}
            {instagramHandle && (
              <a href={`https://instagram.com/${instagramHandle}`} target="_blank" rel="noreferrer">
                <img src={routes.makePublic('/instagram.png')} alt="Instagram" width="30px" />
              </a>
            )}
          </Box>
        </Box>
        <Typography variant="body1">{description}</Typography>
      </Grid>
    </Grid>
  );
}

export default function TeamView() {
  const teamMembers: readonly Props[] = [
    {
      name: 'Spencer Corwin',
      title: 'Co-Founder, CEO, Software Engineer',
      linkedInUrl: 'https://www.linkedin.com/in/spencercorwin',
      avatarImagePath: routes.makePublic('/spencer-corwin-headshot-450.jpg'),
      description: `
        Spencer is an experienced software engineer and co-founder of Microgreen Manager.
        Spencer left Meta Platforms (previously called Facebook) to create Microgreen Manager, a company that aims to help microgreens farmers
        manage their production and logistics. Spencer has a passion for building high-quality software that
        people love, and he has a growing interest in local, sustainable food production.
      `,
    },
    {
      name: 'Garrett Corwin',
      title: 'Co-Founder, COO, Microgreen Farmer',
      instagramHandle: 'piedmontmicrogreens',
      avatarImagePath: routes.makePublic('/garrett-corwin-headshot-450.jpg'),
      description: `
        Garrett is the owner of Piedmont Microgreens and co-founder of Microgreen Manager.
        Garrett earned his master’s degree from Duke, which focused on improving
        the environmental business practices in for-profit companies.
        He spent two years working on the Duke Farm and a summer interning for Freight Farms.
        The Duke Farm taught Garrett about regenerative agriculture.
        Freight Farms taught him about indoor and high-tech agriculture.
        He has a passion for farming, in all its forms, and teaching others how to grow and sell microgreens.
      `,
    },
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Grid container gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }} display="flex">
        {teamMembers.map((props) => (
          <TeamMemberCard key={props.name} {...props} />
        ))}
      </Grid>
    </Box>
  );
}
